// Outlet colour coding — the "Colour guide" above an outlet list.
//
// One chip per colour with how many outlets in the current list have it
// (plus "No colour" when some have none). Tapping a chip shows only those
// outlets; tapping it again, or "All", shows everything. Hidden entirely
// when there are no colours for this user.
import { NO_COLOUR } from "@/lib/outletColors";

export default function OutletColorLegend({ legend, counts, active, onChange, testid = "outlet-color-legend" }) {
  if (!legend || legend.length === 0) return null;
  const n = (k) => counts?.[k] || 0;
  const none = n(NO_COLOUR);
  const toggle = (k) => onChange?.(active === k ? null : k);
  return (
    <div className="min-w-0 space-y-1.5" data-testid={testid}>
      <div className="text-[11px] text-neutral-500">
        Colour guide <span className="text-neutral-400">· tap a colour to see only those outlets</span>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
        <Pill label="All" selected={!active} onClick={() => onChange?.(null)}
              testid={`${testid}-all`} />
        {legend.map((r) => (
          <Pill key={r.id} label={r.name} dot={r.color} count={n(r.id)}
                selected={active === r.id} onClick={() => toggle(r.id)}
                testid={`${testid}-${r.id.slice(0, 8)}`} />
        ))}
        {(none > 0 || active === NO_COLOUR) && (
          <Pill label="No colour" dot="none" count={none}
                selected={active === NO_COLOUR} onClick={() => toggle(NO_COLOUR)}
                testid={`${testid}-none`} />
        )}
      </div>
    </div>
  );
}

// dot: "#RRGGBB" for a colour, "none" for a hollow dot, undefined for no dot.
function Pill({ label, dot, count, selected, onClick, testid }) {
  const coloured = dot && dot !== "none";
  const selectedCls = coloured ? "font-semibold"
    : dot === "none" ? "border-neutral-500 bg-neutral-100 text-neutral-800 font-semibold"
    : "border-[#6D2C91] bg-[#6D2C91]/10 text-[#6D2C91] font-semibold";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      data-testid={testid}
      className={"inline-flex max-w-full min-w-0 items-center gap-1 rounded-full border px-2 py-1 text-[11px] leading-tight "
        + (selected ? selectedCls : "border-neutral-200 bg-white text-neutral-700")
        + (count === 0 && !selected ? " opacity-60" : "")}
      style={selected && coloured ? { backgroundColor: `${dot}1F`, borderColor: dot, color: dot } : undefined}
    >
      {coloured && <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: dot }} />}
      {dot === "none" && <span className="h-2 w-2 shrink-0 rounded-full border border-neutral-400" />}
      <span className="min-w-0 truncate">{label}</span>
      {count != null && (
        <span className={"shrink-0 tabular-nums " + (selected ? "" : "text-neutral-400")}>{count}</span>
      )}
    </button>
  );
}
